import { locale, t, type MessageKey } from "../i18n";

/**
 * How Meta billed a message, in words: the estimated cost with its category and market, or why
 * it was free. Markets come from Meta's rate cards in English; countries are named by the browser,
 * regions by the locale files.
 */
export interface MessagePricing {
  category: string | null;
  type: string | null;
  cost: {
    micros: number;
    currency: string;
    market: string | null;
    /** The rate-card estimate, when Meta's daily value replaced it. */
    estimatedMicros?: number;
    /** When Meta's daily pricing confirmed the value. */
    confirmedAt?: string | null;
  } | null;
}

/** From this day Meta's free service messages are the 1,000 per number per month. */
export const SERVICE_CHARGING_STARTS = "2026-10-01";

const COUNTRY_CODES: Record<string, string> = {
  Argentina: "AR",
  Bangladesh: "BD",
  Brazil: "BR",
  Chile: "CL",
  Colombia: "CO",
  Egypt: "EG",
  France: "FR",
  Germany: "DE",
  "Hong Kong": "HK",
  Hungary: "HU",
  India: "IN",
  Indonesia: "ID",
  Iraq: "IQ",
  Israel: "IL",
  Italy: "IT",
  Kazakhstan: "KZ",
  Kuwait: "KW",
  Malaysia: "MY",
  Mexico: "MX",
  Morocco: "MA",
  Nepal: "NP",
  Netherlands: "NL",
  Nigeria: "NG",
  Oman: "OM",
  Pakistan: "PK",
  Peru: "PE",
  Poland: "PL",
  Qatar: "QA",
  Romania: "RO",
  Russia: "RU",
  "Saudi Arabia": "SA",
  Singapore: "SG",
  "South Africa": "ZA",
  Spain: "ES",
  "Sri Lanka": "LK",
  Turkey: "TR",
  Ukraine: "UA",
  "United Arab Emirates": "AE",
  "United Kingdom": "GB",
};
const REGIONS: Record<string, string> = {
  "North America": "north_america",
  "Rest of Africa": "rest_of_africa",
  "Rest of Asia Pacific": "rest_of_asia_pacific",
  "Rest of Central & Eastern Europe": "rest_of_central_eastern_europe",
  "Rest of Latin America": "rest_of_latin_america",
  "Rest of Middle East": "rest_of_middle_east",
  "Rest of Western Europe": "rest_of_western_europe",
  Other: "other",
};

export function metaMarketLabel(market: string): string {
  const region = REGIONS[market];
  if (region) return t(`meta.market.${region}` as MessageKey);
  const code = COUNTRY_CODES[market];
  if (!code) return market;
  try {
    return (
      new Intl.DisplayNames([locale.value], { type: "region" }).of(code) ??
      market
    );
  } catch {
    return market;
  }
}

/** Group sends are billed like their 1:1 category. */
export function metaCategoryLabel(category: string): string {
  const base = category.replace(/^group_/, "");
  const key = `meta.pricing.category.${base}` as MessageKey;
  const text = t(key);
  return text === key ? category : text;
}

/** Per-message amounts keep Meta's four decimals (R$ 0,035); totals round to cents. */
export function metaMoney(
  micros: number,
  currency: string,
  precise = false,
): string {
  const value = micros / 1e6;
  return new Intl.NumberFormat(locale.value, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: precise && value !== 0 && value < 1 ? 4 : 2,
  }).format(value);
}

/** The line under "Cobrança da Meta", or null when Meta has not said anything yet. */
export function metaChargeText(
  p: MessagePricing,
  sentAt?: string,
): string | null {
  if (p.cost)
    return [
      metaMoney(p.cost.micros, p.cost.currency, true),
      p.category ? metaCategoryLabel(p.category) : null,
      p.cost.market ? metaMarketLabel(p.cost.market) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  if (
    p.type === "free_customer_service" ||
    p.type === "free_group_customer_service"
  )
    return sentAt && sentAt.slice(0, 10) >= SERVICE_CHARGING_STARTS
      ? t("meta.pricing.free_quota")
      : t("meta.pricing.free_customer_service");
  if (p.type === "free_entry_point" || p.category === "referral_conversion")
    return t("meta.pricing.free_entry_point");
  // Charged, but the account has no currency with a published card here.
  if (p.type === "regular" && p.category)
    return t("meta.pricing.charged_unknown", {
      category: metaCategoryLabel(p.category),
    });
  return null;
}
